import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useI18n } from "@/i18n/LanguageContext";
import LangSuggest from "@/components/LangSuggest";
import { ConfirmProvider } from "@/components/ui/confirm-dialog";
import { langFromPath, ROUTES } from "@/lib/routes";
import { track, EVENTS } from "@/lib/analytics";
import Seo from "@/components/Seo";
import { META } from "@/lib/seo-meta";
import Landing from "@/pages/Landing";
import Booking from "@/pages/Booking";
import PaymentReturn from "@/pages/PaymentReturn";
import Legal from "@/pages/Legal";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Services from "@/pages/public/Services";
import Events from "@/pages/public/Events";
import EventsCapabilities from "@/pages/public/EventsCapabilities";
import EventDetail from "@/pages/public/EventDetail";
import Ventures from "@/pages/public/Ventures";
import Technology from "@/pages/public/Technology";
import Work from "@/pages/public/Work";
import Industries from "@/pages/public/Industries";
import Products from "@/pages/public/Products";
import ProductDetail from "@/pages/public/ProductDetail";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import Faq from "@/pages/public/Faq";
import NotFound from "@/pages/public/NotFound";
import CookieConsent from "@/components/CookieConsent";

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

function SmoothScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Lenis global smooth-scroll is only for the cinematic public pages.
    // Skipped on admin (breaks inner scroll) and for users preferring reduced motion.
    if (pathname.startsWith("/blueprint-os")) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [pathname]);
  return null;
}

// Keeps the i18n language in sync with the URL (/ = en, /es/* = es).
function LangSync() {
  const { pathname } = useLocation();
  const { lang, changeLang } = useI18n();
  useEffect(() => {
    if (pathname.startsWith("/blueprint-os")) return;
    const urlLang = langFromPath(pathname);
    if (urlLang !== lang) changeLang(urlLang);
  }, [pathname, lang, changeLang]);
  return null;
}

function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => { track(EVENTS.PAGE_VIEW, { path: pathname }); }, [pathname]);
  return null;
}

function AdminGate() {
  const { user, checked } = useAuth();
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);
  if (!checked || user === null) {
    return <div className="min-h-screen bg-[#0a0a0b]" />;
  }
  return user ? <AdminDashboard /> : <AdminLogin />;
}

function GlobalCookie() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/blueprint-os")) return null;
  return <CookieConsent />;
}

const LegalPage = ({ type, pageKey, lang }) => {
  const m = META[pageKey][lang];
  return (
    <>
      <Seo pageKey={pageKey} lang={lang} title={m.title} description={m.description} />
      <Legal type={type} />
    </>
  );
};

const BookPage = ({ lang }) => {
  const m = META.book[lang];
  return (
    <>
      <Seo pageKey="book" lang={lang} title={m.title} description={m.description} image={m.image} />
      <Booking />
    </>
  );
};

function AppRoutes() {
  useI18n(); // subscribes the route tree to language/locale-data changes (lazy-loaded locales)
  return (
          <Suspense fallback={<div className="min-h-screen bg-white" />}>
          <Routes>
            {/* English (root, x-default) */}
            <Route path="/" element={<Landing />} />
            <Route path={ROUTES.services.en} element={<Services lang="en" />} />
            <Route path={ROUTES.events.en} element={<Events lang="en" />} />
            <Route path={ROUTES.eventsCapabilities.en} element={<EventsCapabilities lang="en" />} />
            <Route path={`${ROUTES.events.en}/:slug`} element={<EventDetail lang="en" />} />
            <Route path={ROUTES.ventures.en} element={<Ventures lang="en" />} />
            <Route path={ROUTES.technology.en} element={<Technology lang="en" />} />
            <Route path={ROUTES.work.en} element={<Work lang="en" />} />
            <Route path={ROUTES.industries.en} element={<Industries lang="en" />} />
            <Route path={ROUTES.products.en} element={<Products lang="en" />} />
            <Route path={ROUTES.productArena.en} element={<ProductDetail lang="en" productKey="productArena" />} />
            <Route path={ROUTES.productCentauros.en} element={<ProductDetail lang="en" productKey="productCentauros" />} />
            <Route path={ROUTES.productAos.en} element={<ProductDetail lang="en" productKey="productAos" />} />
            <Route path={ROUTES.about.en} element={<About lang="en" />} />
            <Route path={ROUTES.contact.en} element={<Contact lang="en" />} />
            <Route path={ROUTES.faq.en} element={<Faq lang="en" />} />
            <Route path={ROUTES.book.en} element={<BookPage lang="en" />} />
            <Route path={ROUTES.blog.en} element={<Blog lang="en" />} />
            <Route path="/blog/:slug" element={<BlogArticle lang="en" />} />
            <Route path={ROUTES.privacy.en} element={<LegalPage type="privacidad" pageKey="privacy" lang="en" />} />
            <Route path={ROUTES.cookies.en} element={<LegalPage type="cookies" pageKey="cookies" lang="en" />} />
            <Route path={ROUTES.terms.en} element={<LegalPage type="terminos" pageKey="terms" lang="en" />} />

            {/* Spanish (/es prefix) */}
            <Route path="/es" element={<Landing />} />
            <Route path={ROUTES.services.es} element={<Services lang="es" />} />
            <Route path={ROUTES.events.es} element={<Events lang="es" />} />
            <Route path={ROUTES.eventsCapabilities.es} element={<EventsCapabilities lang="es" />} />
            <Route path={`${ROUTES.events.es}/:slug`} element={<EventDetail lang="es" />} />
            <Route path={ROUTES.ventures.es} element={<Ventures lang="es" />} />
            <Route path={ROUTES.technology.es} element={<Technology lang="es" />} />
            <Route path={ROUTES.work.es} element={<Work lang="es" />} />
            <Route path={ROUTES.industries.es} element={<Industries lang="es" />} />
            <Route path={ROUTES.products.es} element={<Products lang="es" />} />
            <Route path={ROUTES.productArena.es} element={<ProductDetail lang="es" productKey="productArena" />} />
            <Route path={ROUTES.productCentauros.es} element={<ProductDetail lang="es" productKey="productCentauros" />} />
            <Route path={ROUTES.productAos.es} element={<ProductDetail lang="es" productKey="productAos" />} />
            <Route path={ROUTES.about.es} element={<About lang="es" />} />
            <Route path={ROUTES.contact.es} element={<Contact lang="es" />} />
            <Route path={ROUTES.faq.es} element={<Faq lang="es" />} />
            <Route path={ROUTES.book.es} element={<BookPage lang="es" />} />
            <Route path={ROUTES.blog.es} element={<Blog lang="es" />} />
            <Route path="/es/blog/:slug" element={<BlogArticle lang="es" />} />
            <Route path={ROUTES.privacy.es} element={<LegalPage type="privacidad" pageKey="privacy" lang="es" />} />
            <Route path={ROUTES.cookies.es} element={<LegalPage type="cookies" pageKey="cookies" lang="es" />} />
            <Route path={ROUTES.terms.es} element={<LegalPage type="terminos" pageKey="terms" lang="es" />} />

            {/* FR / DE / IT / PT (prefixed) */}
            {["fr", "de", "it", "pt"].flatMap((l) => [
              <Route key={`${l}-home`} path={`/${l}`} element={<Landing />} />,
              <Route key={`${l}-services`} path={ROUTES.services[l]} element={<Services lang={l} />} />,
              <Route key={`${l}-events`} path={ROUTES.events[l]} element={<Events lang={l} />} />,
              <Route key={`${l}-events-cap`} path={ROUTES.eventsCapabilities[l]} element={<EventsCapabilities lang={l} />} />,
              <Route key={`${l}-event-detail`} path={`${ROUTES.events[l]}/:slug`} element={<EventDetail lang={l} />} />,
              <Route key={`${l}-ventures`} path={ROUTES.ventures[l]} element={<Ventures lang={l} />} />,
              <Route key={`${l}-technology`} path={ROUTES.technology[l]} element={<Technology lang={l} />} />,
              <Route key={`${l}-work`} path={ROUTES.work[l]} element={<Work lang={l} />} />,
              <Route key={`${l}-industries`} path={ROUTES.industries[l]} element={<Industries lang={l} />} />,
              <Route key={`${l}-products`} path={ROUTES.products[l]} element={<Products lang={l} />} />,
              <Route key={`${l}-arena`} path={ROUTES.productArena[l]} element={<ProductDetail lang={l} productKey="productArena" />} />,
              <Route key={`${l}-centauros`} path={ROUTES.productCentauros[l]} element={<ProductDetail lang={l} productKey="productCentauros" />} />,
              <Route key={`${l}-aos`} path={ROUTES.productAos[l]} element={<ProductDetail lang={l} productKey="productAos" />} />,
              <Route key={`${l}-about`} path={ROUTES.about[l]} element={<About lang={l} />} />,
              <Route key={`${l}-contact`} path={ROUTES.contact[l]} element={<Contact lang={l} />} />,
              <Route key={`${l}-faq`} path={ROUTES.faq[l]} element={<Faq lang={l} />} />,
              <Route key={`${l}-book`} path={ROUTES.book[l]} element={<BookPage lang={l} />} />,
              <Route key={`${l}-blog`} path={ROUTES.blog[l]} element={<Blog lang={l} />} />,
              <Route key={`${l}-blog-art`} path={`/${l}/blog/:slug`} element={<BlogArticle lang={l} />} />,
              <Route key={`${l}-privacy`} path={ROUTES.privacy[l]} element={<LegalPage type="privacidad" pageKey="privacy" lang={l} />} />,
              <Route key={`${l}-cookies`} path={ROUTES.cookies[l]} element={<LegalPage type="cookies" pageKey="cookies" lang={l} />} />,
              <Route key={`${l}-terms`} path={ROUTES.terms[l]} element={<LegalPage type="terminos" pageKey="terms" lang={l} />} />,
              <Route key={`${l}-pago`} path={`/${l}/pago`} element={<PaymentReturn />} />,
            ])}

            {/* Payment return (noindex, both languages share it) */}
            <Route path="/pago" element={<PaymentReturn />} />
            <Route path="/es/pago" element={<PaymentReturn />} />

            {/* Legacy URL redirects (SPA-level) */}
            <Route path="/reservar" element={<Navigate to="/es/reservar" replace />} />
            <Route path="/privacidad" element={<Navigate to="/es/privacidad" replace />} />
            <Route path="/terminos" element={<Navigate to="/es/terminos" replace />} />

            {/* Admin (noindex) */}
            <Route
              path="/blueprint-os"
              element={
                <AuthProvider>
                  <AdminGate />
                </AuthProvider>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <ConfirmProvider>
        <Toaster position="top-center" theme="dark" />
        <BrowserRouter>
          <a href="#main" className="skip-link">Skip to content</a>
          <SmoothScroll />
          <LangSync />
          <RouteTracker />
          <GlobalCookie />
          <LangSuggest />
          <AppRoutes />
        </BrowserRouter>
        </ConfirmProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
